/* eslint-disable camelcase */
import { trackPage, trackClick, trackPublisher } from ".";
import PagesEnum from "./enums/pages";
import ChaptersEnum from "./enums/chapters";
import LevelsEnum from "./enums/levels";
import ClickTypes from "./enums/click-types";
import ClicksEnum from "./enums/clicks";
import { Publisher } from "../Interfaces/tracking";

interface RequestStateParams {
  state: keyof typeof ChaptersEnum;
}

interface RequestParams {
  family?: string | null;
  subFamily?: string | null;
  requestId?: string | null;
  vendorId?: string | null;
  status?: string | null;
}

interface RecommendationParams {
  status?: string | null;
  requestId?: string | null;
  recommendationId?: string | null;
  vendorId?: string | null;
}

interface RecommendationPublisherParams {
  pageSlug: string;
  format?: boolean;
  url?: string;
  contentId?: string;
  positionItem: number;
  nbItems: number;
  memberId?: string;
  vendorId?: string;
}

// Pages tracking

export function trackPageRequests({ state }: RequestStateParams) {
  return trackPage({
    page: PagesEnum.Requests,
    page_chapter1: ChaptersEnum.Requests,
    page_chapter2: ChaptersEnum[state],
    site_level2: LevelsEnum.MyAccount,
  });
}

export function trackPageRequestDetail({
  family,
  subFamily,
  requestId,
  vendorId,
  status,
}: RequestParams) {
  return trackPage({
    page: PagesEnum.RequestDetail,
    page_chapter1: ChaptersEnum.RequestDetail,
    page_chapter2: `${family}|${subFamily}`,
    page_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    vendorId,
    requestId,
  });
}

export function trackPageRequestRecommendations({
  family,
  subFamily,
  requestId,
  proposalId,
  vendorId,
  status,
}: RequestParams & { proposalId: string }) {
  return trackPage({
    page: PagesEnum.RequestRecommendations,
    page_chapter1: ChaptersEnum.RequestRecommendations,
    page_chapter2: `${family}|${subFamily}`,
    page_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    requestId,
    contentId: proposalId,
    vendorId,
  });
}

export function trackPageRecommendationDetail({
  status,
  requestId,
  recommendationId,
  vendorId,
}: RecommendationParams) {
  return trackPage({
    page: PagesEnum.Recommendation,
    page_chapter1: ChaptersEnum.RequestRecommendationDetail,
    page_chapter2: status,
    site_level2: LevelsEnum.MyAccount,
    requestId,
    contentId: recommendationId,
    vendorId,
  });
}

export const trackRecommendationPublisher = ({
  pageSlug,
  format = true,
  url = "",
  contentId = "",
  positionItem,
  nbItems,
  memberId = "",
  vendorId = "",
}: RecommendationPublisherParams): Publisher => {
  let generalPlacement = `[${positionItem}]`;
  if (nbItems) {
    generalPlacement = `[${positionItem + 1}-${nbItems}]`;
  }

  const payload: Publisher = {
    onsitead_campaign: `[${pageSlug}::::${pageSlug}]`,
    onsitead_creation: `[${url || "no_url"}]`,
    onsitead_variant: `[${contentId}]`,
    onsitead_format: `[${format ? "Yes" : "No"}]`,
    onsitead_general_placement: generalPlacement,
    onsitead_detailed_placement: `[${memberId}]`,
    onsitead_advertiser: `[${vendorId}]`,
    onsitead_url: url,
  };

  return trackPublisher("publisher.impression", payload);
};

// Clicks tracking

export function trackClickRequests({ state }: RequestStateParams) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.Requests,
    click_chapter1: ChaptersEnum.Requests,
    click_chapter2: ChaptersEnum[state],
    click_chapter3: ChaptersEnum.Header,
    site_level2: LevelsEnum.MyAccount,
  });
}

export function trackClickRequestDetail({
  family,
  subFamily,
  vendorId,
  requestId,
  status,
}: RequestParams) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.RequestDetail,
    click_chapter1: ChaptersEnum.RequestDetail,
    click_chapter2: `${family}|${subFamily}`,
    click_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    vendorId,
    requestId,
  });
}

export function trackClickRequestDetailCTA({
  state,
  family,
  subFamily,
  vendorId,
  requestId,
  status,
}: RequestParams & { state: keyof typeof ChaptersEnum & keyof typeof ClicksEnum }) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum[state],
    click_chapter1: ChaptersEnum[state],
    click_chapter2: `${family}|${subFamily}`,
    click_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    vendorId,
    requestId,
  });
}

export function trackClickRequestRecommendation({
  family,
  subFamily,
  requestId,
  vendorId,
  status,
}: RequestParams) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.RequestRecommendationDetail,
    click_chapter1: ChaptersEnum.RequestRecommendations,
    click_chapter2: `${family}|${subFamily}`,
    click_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    requestId,
    vendorId,
  });
}

export function trackClickRequestRejectProposal({
  family,
  subFamily,
  requestId,
  status,
}: Omit<RequestParams, "vendorId">) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.RequestRejectProposal,
    click_chapter1: ChaptersEnum.RequestRejectProposal,
    click_chapter2: `${family}|${subFamily}`,
    click_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    requestId,
  });
}

export function trackClickShowPreviousProposals({
  family,
  subFamily,
  requestId,
  status,
}: Omit<RequestParams, "vendorId">) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.RequestPreviousProposals,
    click_chapter1: ChaptersEnum.RequestPreviousProposals,
    click_chapter2: `${family}|${subFamily}`,
    click_chapter3: status,
    site_level2: LevelsEnum.MyAccount,
    requestId,
  });
}

export function trackClickRequestChooseRecommendation({
  status,
  requestId,
  recommendationId,
  vendorId,
}: RecommendationParams) {
  return trackClick({
    click_type: ClickTypes.Action,
    click: ClicksEnum.RequestChooseRecommendation,
    click_chapter1: ChaptersEnum.RequestRecommendationDetail,
    click_chapter2: status,
    requestId,
    contentId: recommendationId,
    vendorId,
  });
}

export function trackClickRequestMapsRecommendation({
  status,
  requestId,
  recommendationId,
  vendorId,
}: RecommendationParams) {
  return trackClick({
    click_type: ClickTypes.Navigation,
    click: ClicksEnum.RequestMapsRecommendation,
    click_chapter1: ChaptersEnum.RequestRecommendationDetail,
    click_chapter2: status,
    requestId,
    contentId: recommendationId,
    vendorId,
  });
}
